/**
 * BudgetTable Component
 * Main data grid for budget line items with grouping support
 */

import React, { useState, useMemo } from 'react';
import { Settings } from 'lucide-react';
import type { BudgetLineItem, BudgetGroupBy, GroupedLineItems } from '../../types/budget';
import BudgetRow from './BudgetRow';
import CategoryRow from './CategoryRow';
import BudgetFooter from './BudgetFooter';

export interface BudgetTableProps {
  lineItems: BudgetLineItem[];
  groupBy: BudgetGroupBy;
  onEdit?: (lineItem: BudgetLineItem) => void;
  onForecast?: (lineItem: BudgetLineItem) => void;
  isLoading?: boolean;
  showColumns?: {
    originalBudget?: boolean;
    approvedChanges?: boolean;
    revisedBudget?: boolean;
    committed?: boolean;
    actual?: boolean;
    forecast?: boolean;
    variance?: boolean;
    percentUsed?: boolean;
  };
}

const BudgetTable: React.FC<BudgetTableProps> = ({
  lineItems,
  groupBy,
  onEdit,
  onForecast,
  isLoading = false,
  showColumns = {
    originalBudget: true,
    approvedChanges: true,
    revisedBudget: true,
    committed: true,
    actual: true,
    forecast: true,
    variance: true,
    percentUsed: true,
  },
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showColumnConfig, setShowColumnConfig] = useState(false);

  // Group line items
  const groupedItems = useMemo((): GroupedLineItems[] => {
    if (groupBy === 'none') {
      return [
        {
          groupKey: 'all',
          groupLabel: 'All Items',
          items: lineItems,
          summary: calculateSummary(lineItems),
        },
      ];
    }

    const groups = new Map<string, BudgetLineItem[]>();
    const groupKey = groupBy === 'category' ? 'category' : 'division';

    lineItems.forEach(item => {
      const key = (item[groupKey] || 'Uncategorized') as string;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    });

    return Array.from(groups.entries()).map(([key, items]) => ({
      groupKey: key,
      groupLabel: key,
      items,
      summary: calculateSummary(items),
    }));
  }, [lineItems, groupBy]);

  // Calculate totals
  const totals = useMemo(() => calculateSummary(lineItems), [lineItems]);

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Loading budget data...</p>
        </div>
      </div>
    );
  }

  if (lineItems.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-12 text-center">
          <p className="text-sm text-gray-500">No line items found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Table Controls */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing <span className="font-medium text-gray-900">{lineItems.length}</span> line items
        </div>
        <button
          onClick={() => setShowColumnConfig(!showColumnConfig)}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Settings className="h-4 w-4" />
          <span>Columns</span>
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Code
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                Description
              </th>
              {showColumns.originalBudget && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Original
                </th>
              )}
              {showColumns.approvedChanges && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Changes
                </th>
              )}
              {showColumns.revisedBudget && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Revised
                </th>
              )}
              {showColumns.committed && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Committed
                </th>
              )}
              {showColumns.actual && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Actual
                </th>
              )}
              {showColumns.forecast && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Forecast
                </th>
              )}
              {showColumns.variance && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Variance
                </th>
              )}
              {showColumns.percentUsed && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                  % Used
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groupedItems.map(group => (
              <React.Fragment key={group.groupKey}>
                {groupBy !== 'none' && (
                  <CategoryRow
                    groupLabel={group.groupLabel}
                    summary={group.summary}
                    isExpanded={expandedGroups.has(group.groupKey)}
                    onToggle={() => toggleGroup(group.groupKey)}
                    itemCount={group.items.length}
                  />
                )}
                {(groupBy === 'none' || expandedGroups.has(group.groupKey)) &&
                  group.items.map(lineItem => (
                    <BudgetRow
                      key={lineItem._id}
                      lineItem={lineItem}
                      onEdit={onEdit}
                      onForecast={onForecast}
                    />
                  ))}
              </React.Fragment>
            ))}
          </tbody>
          <BudgetFooter
            totalOriginalBudget={totals.originalBudget}
            totalApprovedChanges={totals.approvedChanges}
            totalRevisedBudget={totals.revisedBudget}
            totalCommitted={totals.committed}
            totalActual={totals.actual}
            totalForecast={lineItems.reduce((sum, item) => sum + item.forecastAtCompletion, 0)}
            totalVariance={totals.variance}
            totalVariancePercent={
              totals.revisedBudget > 0 ? (totals.variance / totals.revisedBudget) * 100 : 0
            }
          />
        </table>
      </div>
    </div>
  );
};

// Helper function to calculate summary
function calculateSummary(items: BudgetLineItem[]) {
  return items.reduce(
    (summary, item) => ({
      originalBudget: summary.originalBudget + item.originalBudget,
      approvedChanges: summary.approvedChanges + item.approvedChanges,
      revisedBudget: summary.revisedBudget + item.revisedBudget,
      committed: summary.committed + item.committed,
      actual: summary.actual + item.actual,
      variance: summary.variance + item.variance,
    }),
    {
      originalBudget: 0,
      approvedChanges: 0,
      revisedBudget: 0,
      committed: 0,
      actual: 0,
      variance: 0,
    }
  );
}

export default BudgetTable;
